/**
 * Carte « Météo » de l'accueil : GET /v1/meteo (Open-Meteo via l'API, cache 30 min, voir packages/shared/src/meteo.ts).
 * Lieu (décision du 27/10/2026) : la position de l'utilisateur si le navigateur la donne (autorisation demandée une
 * fois), sinon la ville de la filiale courante, et la France (FR01) en vue « Toutes mes filiales ». La position est
 * arrondie au dixième de degré (≈ 10 km) AVANT l'envoi. Unités impériales pour la filiale des États-Unis. La carte
 * affiche d'abord la ville de la filiale, puis la position dès qu'elle est connue (sans squelette entre les deux).
 * Alertes de vigilance : à venir, avec le planning des interventions.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  COORDONNEES_FILIALES, FRAICHEUR_METEO_MS, PICTO_METEO, SOURCES_METEO, TOUTES_FILIALES, arrondirCoordonnee, cleLibre,
  filiale as trouverFiliale, temperatureEn, unitesMeteo, ventEn, type Meteo, type ModeWidget,
} from '@agora/shared';
import { Skeleton, SkeletonGroup, WeatherWidget, Widget, type WidgetProps } from '../../design';
import { ErreurApi, useApi, useFiliale, useFormats, useT } from '../../socle';

type Lieu = { lat: number; lon: number };
/** Filiale de la vue consolidée (« Toutes mes filiales ») : la France. */
const FILIALE_PAR_DEFAUT = 'FR01';
const SYMBOLES = { metriques: '°C', imperiales: '°F' } as const;

/** Position du navigateur, arrondie ; null tant qu'elle n'est pas connue (en attente, refusée ou indisponible). */
function usePosition(): Lieu | null {
  const [position, setPosition] = React.useState<Lieu | null>(null);
  React.useEffect(() => {
    if (!('geolocation' in navigator)) return;
    let actif = true;
    navigator.geolocation.getCurrentPosition(
      (p) => { if (actif) setPosition({ lat: arrondirCoordonnee(p.coords.latitude), lon: arrondirCoordonnee(p.coords.longitude) }); },
      () => undefined,   // refus ou position indisponible : la ville de la filiale reste affichée
      { enableHighAccuracy: false, maximumAge: FRAICHEUR_METEO_MS, timeout: 15_000 },
    );
    return () => { actif = false; };
  }, []);
  return position;
}

const memeZone = (a: Lieu, b: Lieu) => arrondirCoordonnee(a.lat) === arrondirCoordonnee(b.lat) && arrondirCoordonnee(a.lon) === arrondirCoordonnee(b.lon);

export function CarteMeteo({ mode, widget }: { mode: ModeWidget; widget: Omit<WidgetProps, 'children' | 'extra'> }) {
  const { t, locale } = useT();
  const f = useFormats();
  const api = useApi();
  const { filiale } = useFiliale();
  const position = usePosition();
  const code = filiale === TOUTES_FILIALES ? FILIALE_PAR_DEFAUT : filiale;
  const lieu = position ?? COORDONNEES_FILIALES[code] ?? COORDONNEES_FILIALES[FILIALE_PAR_DEFAUT];
  const q = useQuery({
    queryKey: ['meteo', arrondirCoordonnee(lieu.lat), arrondirCoordonnee(lieu.lon)],
    queryFn: ({ signal }) => api.get<Meteo>('/v1/meteo', { lat: lieu.lat, lon: lieu.lon }, signal),
    staleTime: FRAICHEUR_METEO_MS,
    refetchInterval: FRAICHEUR_METEO_MS,
    // Changement de lieu : la météo affichée reste à l'écran jusqu'à l'arrivée de la nouvelle.
    placeholderData: (precedent) => precedent,
  });
  const m = q.data;
  const source = m ? SOURCES_METEO[m.source] : undefined;
  const mention = source && (
    <a className="ag-login-link" href={source.url} target="_blank" rel="noreferrer" aria-label={t('accueil.meteoSourceLien', { source: source.nom })}>
      {t('accueil.meteoSource', { source: source.nom })}
    </a>
  );

  let contenu: React.ReactNode;
  if (!m) {
    contenu = q.isError
      ? <p className="agx-vide" role="status">{t(`erreurs.${q.error instanceof ErreurApi ? q.error.code : 'interne'}`)}</p>
      : (
        <SkeletonGroup label={t('app.chargement')}>
          <Skeleton variant="title" width={40} />
          <Skeleton width={70} />
        </SkeletonGroup>
      );
  } else {
    // Libellé du lieu des données affichées (et non du lieu demandé, qui peut être encore en chargement).
    const surPosition = !!position && memeZone(position, m);
    const ville = surPosition ? undefined
      : trouverFiliale(Object.keys(COORDONNEES_FILIALES).find((c) => memeZone(COORDONNEES_FILIALES[c], m)) ?? code);
    const unites = unitesMeteo(trouverFiliale(code));
    const temp = (celsius: number) => Math.round(temperatureEn(celsius, unites));
    const jour = (date: string) => new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
    const pluie = m.jours[0]?.pluie;
    contenu = (
      <WeatherWidget
        mode={mode}
        place={surPosition ? t('accueil.meteoMaPosition') : ville?.ville ?? ''}
        siteLabel={ville ? t(cleLibre('pays', ville.pays)) : undefined}
        temp={temp(m.actuel.temperature)} unit={SYMBOLES[unites]}
        condition={t(`accueil.meteoConditions.${m.actuel.condition}`)} kind={PICTO_METEO[m.actuel.condition]}
        details={[
          { label: t('accueil.meteoVent'), value: f.nombre(Math.round(ventEn(m.actuel.vent, unites)), { style: 'unit', unit: unites === 'imperiales' ? 'mile-per-hour' : 'kilometer-per-hour' }) },
          ...(pluie === null || pluie === undefined ? [] : [{ label: t('accueil.meteoPluie'), value: f.nombre(pluie / 100, { style: 'percent' }) }]),
          { label: t('accueil.meteoRessenti'), value: `${temp(m.actuel.ressenti)}°` },
        ]}
        forecast={m.jours.slice(1, 5).map((j) => ({ day: jour(j.date), kind: PICTO_METEO[j.condition], high: temp(j.max), low: temp(j.min) }))}
      />
    );
  }
  return <Widget {...widget} extra={mention}>{contenu}</Widget>;
}
